import { Activity, Box, Lamp, Plug, Thermometer } from "lucide-react";
import { ClimateTile, DeviceTile, DimmerSlider, MediaTile } from "@/components/system";
import { toMediaVM } from "@/lib/homey-viewmodel";
import type { DeviceTileVM, HomeyDeviceSnap } from "@/types/homey";
import type { useDeviceCommands } from "./useDeviceCommands";

interface DeviceTileForProps {
  vm: DeviceTileVM;
  /**
   * Raw device snapshot. Required for `media` tiles, which derive a MediaVM
   * (now-playing + control capability set) that the DeviceTileVM doesn't carry.
   */
  device?: HomeyDeviceSnap;
  commands: ReturnType<typeof useDeviceCommands>;
}

/** Maps a D-pad direction to its remote_* capability id. */
const DPAD_CAP: Record<"up" | "down" | "left" | "right" | "select", string> = {
  up: "remote_up",
  down: "remote_down",
  left: "remote_left",
  right: "remote_right",
  select: "remote_select",
};

/**
 * Renders the correct design-system tile for a given DeviceTileVM kind.
 *
 * Domain mapping per plan:
 *   light   -> light
 *   climate -> climate
 *   socket  -> energy
 *   media   -> neutral (opens a now-playing control dialog)
 *   sensor  -> climate (read-only)
 *   other   -> neutral
 */
export function DeviceTileFor({ vm, device, commands }: DeviceTileForProps) {
  switch (vm.kind) {
    case "light":
      return (
        <DeviceTile
          name={vm.name}
          icon={<Lamp />}
          domain="light"
          state={vm.state}
          active={vm.active}
          available={vm.available}
          onToggle={vm.togglable ? () => commands.toggle(vm.id, vm.active) : undefined}
        >
          {vm.dim !== null && vm.active && (
            <DimmerSlider
              value={vm.dim}
              domain="light"
              aria-label={`Dim ${vm.name}`}
              onChange={(v) => commands.dim(vm.id, v)}
              onCommit={(v) => commands.commitDim(vm.id, v)}
              disabled={!vm.available}
            />
          )}
        </DeviceTile>
      );

    case "climate":
      return (
        <ClimateTile
          name={vm.name}
          state={vm.state}
          active={vm.active}
          available={vm.available}
          targetTemperature={vm.targetTemperature}
          measuredTemperature={vm.measuredTemperature}
          onCommitTarget={(v) => commands.commitTargetTemperature(vm.id, v)}
        />
      );

    case "socket":
      return (
        <DeviceTile
          name={vm.name}
          icon={<Plug />}
          domain="energy"
          state={vm.state}
          active={vm.active}
          available={vm.available}
          onToggle={vm.togglable ? () => commands.toggle(vm.id, vm.active) : undefined}
        />
      );

    case "media": {
      // Without the raw device snapshot we can't derive media facts; fall back
      // to a plain on/off tile (still better than crashing).
      if (!device) {
        return (
          <DeviceTile
            name={vm.name}
            icon={<Box />}
            domain="neutral"
            state={vm.state}
            active={vm.active}
            available={vm.available}
            onToggle={vm.togglable ? () => commands.toggle(vm.id, vm.active) : undefined}
          />
        );
      }
      const media = toMediaVM(device);
      // Play/pause: remote_playpause is a momentary press; speaker_playing is a
      // boolean we flip. Resolved cap id tells us which.
      const handlePlayPause = () => {
        const cap = media.caps.playPause;
        if (!cap) return;
        if (cap === "speaker_playing") {
          commands.setBoolean(media.id, cap, !(media.playing ?? false));
        } else {
          commands.pressMomentary(media.id, cap);
        }
      };
      return (
        <MediaTile
          media={media}
          onPlayPause={handlePlayPause}
          onPrev={() => media.caps.prev && commands.pressMomentary(media.id, media.caps.prev)}
          onNext={() => media.caps.next && commands.pressMomentary(media.id, media.caps.next)}
          onVolume={(v) => commands.commitVolume(media.id, v)}
          onVolumeUp={() => commands.pressMomentary(media.id, "volume_up")}
          onVolumeDown={() => commands.pressMomentary(media.id, "volume_down")}
          onMuteToggle={() => commands.setBoolean(media.id, "volume_mute", !(media.muted ?? false))}
          onShuffleToggle={() =>
            commands.setBoolean(media.id, "speaker_shuffle", !(media.shuffle ?? false))
          }
          onDpad={(dir) => commands.pressMomentary(media.id, DPAD_CAP[dir])}
          onHome={() => commands.pressMomentary(media.id, "remote_home")}
          onBack={() => commands.pressMomentary(media.id, "remote_back")}
        />
      );
    }

    case "sensor":
      // Motion/contact/alarm sensors use the security domain when active;
      // temperature/humidity sensors always use climate.
      return (
        <DeviceTile
          name={vm.name}
          icon={vm.measuredTemperature !== null ? <Thermometer /> : <Activity />}
          domain={vm.active && vm.measuredTemperature === null ? "security" : "climate"}
          state={vm.state}
          active={vm.active}
          available={vm.available}
        />
      );

    case "other":
      return (
        <DeviceTile
          name={vm.name}
          icon={<Box />}
          domain="neutral"
          state={vm.state}
          active={vm.active}
          available={vm.available}
          onToggle={vm.togglable ? () => commands.toggle(vm.id, vm.active) : undefined}
        />
      );
  }
}
